import { HStack, Tabs } from '@chakra-ui/react';
import { CockpitFormSaveDialog } from '@components/cockpit/CockpitFormSaveDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import { PlatformFormMap } from '@components/cockpit/panels/platformManagement/PlatformFormMap';
import PlatformMessagingForm from '@components/cockpit/panels/platformManagement/PlatformMessagingForm';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { selectAllContactsByPlatform } from '@redux/chat/chat.selectors';
import { selectPlatformByCode } from '@redux/config/config.selectors';
import { useUpdateContactsByPlatformMutation } from '@services/chat/chat.api';
import { useCreatePlatformMutation, useUpdatePlatformMutation } from '@services/config/platform.api';
import { areSelectedFieldsEqual } from '@utils/common.utils';
import { platformSchemaValidation } from '@validations/platform.schema';
import { Form, Formik, useFormikContext } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Contact } from '@/types/chat/chat.types';
import { AlertingEventLevelEnum, Platform } from '@/types/config/config.types';

import PlatformDiffusionListForm from './PlatformDiffusionListForm';
import PlatformGeneralForm from './PlatformGeneralForm';

type Props = {
  editedPlatformCode: string | undefined;
  setEditedPlatformCode: Dispatch<SetStateAction<string | null | undefined>>;
};

export type PlatformForm = Partial<Platform> & {
  contacts?: Contact[];
};

const emptyPlatform: PlatformForm = {
  id: undefined,
  name: '',
  heightOffset: 0,
  altitude: null,
  polygon: { type: 'Polygon', coordinates: [] },
  messagingParameters: {
    nfzEntry: {
      enabled: true,
      levels: [AlertingEventLevelEnum.HIGH, AlertingEventLevelEnum.MEDIUM],
    },
    nfzExit: {
      enabled: true,
      delay: 7,
      levels: [AlertingEventLevelEnum.MEDIUM],
    },
    droneLost: {
      enabled: true,
    },
  },
  contacts: [],
};

type PlatformFormContentProps = {
  setShowMapForm: (value: boolean) => void;
};

function PlatformFormContent({ setShowMapForm }: Readonly<PlatformFormContentProps>) {
  const { initialValues, values } = useFormikContext<PlatformForm>();

  return (
    <Tabs.Root orientation="vertical" defaultValue="general" size="md" lazyMount variant="vertical" userSelect="none">
      <Tabs.List>
        <Tabs.Trigger value="general">
          <HStack width="100%">
            <FormattedMessage id="components.platforms.general.title" />
            {!areSelectedFieldsEqual(values, initialValues, ['name', 'altitude', 'heightOffset', 'polygon']) && (
              <ModifiedTag />
            )}
          </HStack>
        </Tabs.Trigger>
        <Tabs.Trigger value="messaging">
          <HStack width="100%">
            <FormattedMessage id="components.platforms.messaging.title" />
            {!areSelectedFieldsEqual(values, initialValues, ['messagingParameters']) && <ModifiedTag />}
          </HStack>
        </Tabs.Trigger>
        <Tabs.Trigger value="diffusionLists">
          <HStack width="100%">
            <FormattedMessage id="components.platforms.diffusionLists.title" />
            {!areSelectedFieldsEqual(values, initialValues, ['contacts']) && <ModifiedTag />}
          </HStack>
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.ContentGroup backgroundColor="neutral.700">
        <Tabs.Content value="general">
          <PlatformGeneralForm setShowMapForm={setShowMapForm} />
        </Tabs.Content>
        <Tabs.Content value="messaging">
          <PlatformMessagingForm />
        </Tabs.Content>
        <Tabs.Content value="diffusionLists" overflow="hidden" overflowY="auto" className="cockpit-scrollbar-900">
          <PlatformDiffusionListForm />
        </Tabs.Content>
      </Tabs.ContentGroup>
    </Tabs.Root>
  );
}

export default function PlatformFormContainer({ editedPlatformCode, setEditedPlatformCode }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const [showMapForm, setShowMapForm] = useState(false);

  const activeSite = useAppSelector(selectActiveSite);
  const platform = useAppSelector((state) => selectPlatformByCode(state, editedPlatformCode ?? '')) ?? emptyPlatform;
  const contacts = useAppSelector((state) => selectAllContactsByPlatform(state, platform.code));
  const [createPlatform, { isLoading: isLoadingCreate }] = useCreatePlatformMutation();
  const [updatePlatform, { isLoading: isLoadingUpdate }] = useUpdatePlatformMutation();
  const [updateContacts, { isLoading: isLoadingUpdateContacts }] = useUpdateContactsByPlatformMutation();
  const isNewPlatform = editedPlatformCode === undefined;
  const validationSchema = useValidation(platformSchemaValidation);

  const initialValues: PlatformForm = { ...platform, contacts };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={(values) => {
        const platform = { ...values };
        const contacts = values.contacts ?? [];
        delete platform.contacts;

        if (isNewPlatform) {
          const platformCode = uuidv4();
          const site = activeSite?.code ?? 'local';
          createPlatform({
            ...platform,
            code: platformCode,
            site,
          })
            .unwrap()
            .then((platform) => {
              setOpen(false);
              createToast(
                formatMessage({ id: 'components.platforms.message.add' }, { name: platform.name }),
                ToastStatusEnum.SUCCESS,
              );
              setEditedPlatformCode(null);
              updateContacts({
                site,
                platformCode,
                contacts: contacts.map((c) => ({ ...c, platform: platformCode })),
              })
                .unwrap()
                .catch(() => {
                  createToast(
                    formatMessage({ id: 'components.platforms.message.contactFailure' }),
                    ToastStatusEnum.WARNING,
                  );
                });
            });
        } else {
          const updatedPlatform = platform as Platform;
          updatePlatform(updatedPlatform)
            .unwrap()
            .then((platform) => {
              setOpen(false);
              createToast(
                formatMessage({ id: 'components.platforms.message.update' }, { name: platform.name }),
                ToastStatusEnum.SUCCESS,
              );
              setEditedPlatformCode(null);
            });
          updateContacts({
            site: updatedPlatform.site,
            platformCode: updatedPlatform.code,
            contacts,
          })
            .unwrap()
            .catch(() => {
              createToast(
                formatMessage({ id: 'components.platforms.message.contactFailure' }),
                ToastStatusEnum.WARNING,
              );
            });
        }
      }}
    >
      {({ initialValues, values, isValid, submitForm }) => {
        const hasChanged = () => {
          return !areSelectedFieldsEqual(values, initialValues, [
            'name',
            'altitude',
            'heightOffset',
            'contacts',
            'messagingParameters',
            'polygon',
          ]);
        };

        return (
          <Form style={{ width: '100%', height: '100%' }} noValidate>
            <CockpitEditorLayout
              dialog={
                <CockpitFormSaveDialog
                  open={open}
                  isLoading={isLoadingCreate || isLoadingUpdate || isLoadingUpdateContacts}
                  confirmLabel={formatMessage({ id: 'global.save' })}
                  cancelLabel={formatMessage({ id: 'global.cancel' })}
                  onConfirm={submitForm}
                  onCancel={() => setOpen(false)}
                  isNewElement={isNewPlatform}
                  name={values.name}
                  saveNewLabel="components.platforms.popup.descriptionSaveNew"
                  saveUpdateLabel="components.platforms.popup.descriptionSave"
                />
              }
              breadcrumbs={
                <Breadcrumb
                  items={[
                    {
                      label: formatMessage({ id: 'components.platforms.title' }),
                      action: () => setEditedPlatformCode(null),
                    },
                    {
                      label: isNewPlatform
                        ? formatMessage({
                            id: 'components.platforms.newPlatform',
                          })
                        : platform.name,
                      action: showMapForm ? () => setShowMapForm(false) : undefined,
                    },
                    ...(showMapForm
                      ? [
                          {
                            label: formatMessage({ id: 'components.platforms.general.perimeterDefinition' }),
                          },
                        ]
                      : []),
                  ]}
                />
              }
              onBack={() => setEditedPlatformCode(null)}
              onSave={() => {
                if (!isValid || !hasChanged()) {
                  return;
                }
                setOpen(true);
              }}
              isSaving={isLoadingCreate || isLoadingUpdate || isLoadingUpdateContacts}
              isSaveDisabled={!isValid || !hasChanged()}
            >
              {showMapForm ? <PlatformFormMap /> : <PlatformFormContent setShowMapForm={setShowMapForm} />}
            </CockpitEditorLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
